import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "More messing with time: Deduping messages between iOS and JavaScript",
  "description": "",
  "published": "2017-07-05T00:00:00.000Z",
  "redirect_from": ["/blog/more-messing-time-deduping-messages-between-ios-and-javascript/swizec/7690"],
  "categories": "Front End, Ruby, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`I've written about time before: `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/a-day-is-not-606024-seconds-long/swizec/6755"
      }}>{`Days are not 60∙60∙24 seconds`}</a>{`, `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/time-funny-ruby/swizec/7525"
      }}>{`Time is funny in Ruby`}</a>{`, and `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/week-7-time-clocks-and-ordering-of-events-in-a-distributed-system/swizec/6444"
      }}>{`Distributed clocks are hard to synchronize`}</a>{`.`}</p>
    <lite-youtube {...{
      "videoid": "edjQCrodD20?ecver=2",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Here's another for the Things You Never Thought Could Go Wrong pile: Deduping messages in a chat application.`}</p>
    <p>{`For months now, we've had this elusive bug at `}<a parentName="p" {...{
        "href": "https://www.yup.com"
      }}>{`Yup`}</a>{` that nobody could figure out. When a student and a tutor are talking, if the tutor refreshes his or her webapp, student messages are doubled. Not because there's a display issue, oh no, because they're all saved twice.`}</p>
    <p>{`The student only sees the bug if they go back to read their session history later.`}</p>
    <p>{`Tutor messages are never doubled.`}</p>
    <p>{`🤔`}</p>
    <p>{`Can you figure it out? Let me give you some background.`}</p>
    <h2 {...{
      "id": "the-background"
    }}>{`The background`}</h2>
    <p>{`Chat sessions happen between a student and a tutor and our server. The server is there to establish a direct connection between student and tutor via a `}<a parentName="p" {...{
        "href": "https://pusher.com/"
      }}>{`Pusher`}</a>{` channel and to store every message in our database. Sometimes it sends system messages.`}</p>
    <p>{`Student talks through an iPhone or Android app, tutor talks through a webapp. Both try to save every message, sent or received, to the server.`}</p>
    <p>{`This 3-way approach ensures the whole session transcript gets saved even if one of the clients loses connection to our server. As long as the two humans can talk to each other and at least one of them can talk to the server, all is well.`}</p>
    <p>{`Sometimes, maybe, the humans can talk and neither of them can talk to the server. If that happens, our service is down. When we get it back, the chat session is hopefully still going and clients can save history.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/07/chart.gif",
        "alt": null
      }}></img></p>
    <p>{`Here's what a typical message looks like:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`{
    chat_id: <number>,
    sent_at: <timestamp>,
    sent_from: <sender>,
    sent_to: <recipient>,
    content_type: <img text="">,
    text: <message>
}
</message></recipient></sender></timestamp></number>
`}</code></pre>
    <p><inlineCode parentName="p">{`chat_id`}</inlineCode>{` tells you which session a message belongs to, `}<inlineCode parentName="p">{`sent_at`}</inlineCode>{` when it was sent, `}<inlineCode parentName="p">{`sent_from`}</inlineCode>{` who sent it, `}<inlineCode parentName="p">{`sent_to`}</inlineCode>{` who's receiving it (because the system can send messages to either human), `}<inlineCode parentName="p">{`content_type`}</inlineCode>{` whether you should render the text or assume it's an image URL, and `}<inlineCode parentName="p">{`text`}</inlineCode>{` gives you the content.`}</p>
    <p>{`On the backend, we rely on a `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Database_index"
      }}>{`database index`}</a>{` to dedupe messages. Our index uses the combination of chat, timestamp, sender, and text to ensure uniqueness.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`add_index "messages", ["chat_id", "text", "sent_at", "sent_from"], name: "messages_must_be_different", unique: true, using: :btree
`}</code></pre>
    <p>{`You can think of this as: In a chatroom, a person can send the same message at the same time only once.`}</p>
    <h2 {...{
      "id": "the-problem"
    }}>{`The problem`}</h2>
    <p>{`So… what's wrong?`}</p>
    <p><img parentName="p" {...{
        "src": "/79226c69eb426ab4106d2ff44be54346/media-3o7btUmopWyjudWcs8-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`If you guessed `}<em parentName="p">{`"iOS and JavaScript round timestamps differently which means the same message saved from a different client has a different `}<inlineCode parentName="em">{`sent_at`}</inlineCode>{`,”`}</em>{` you were right! Congratulations, you're a wizard.`}</p>
    <p><img parentName="p" {...{
        "src": "/2eacd0a5878da40b9eb45808f1f0ae98/media-2AL9zJaTRKds4-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`It was… not the first thing I thought of. 😅`}</p>
    <p>{`Here's a dump from a debugging session on my localhost 👇 Student messages are doubled; that's easy to see. The part that's hard to see is that each student message is saved with two different `}<inlineCode parentName="p">{`sent_at`}</inlineCode>{` timestamps.`}</p>
    <pre><code parentName="pre" {...{}}>{`2.2.0 :001 > puts Session.last.messages.pluck(:sent_from, :sent_at, :text).to_yaml
- - student
  - 2017-07-04 23:00:02.786000000 Z
  - Bdbr
- - student
  - 2017-07-04 23:00:02.786581000 Z
  - Bdbr
- - tutor
  - 2017-07-04 23:00:14.032999000 Z
  - fawefaw
- - tutor
  - 2017-07-04 23:00:14.857000000 Z
  - afeaw
- - student
  - 2017-07-04 23:00:17.053721000 Z
  - Hsj
- - student
  - 2017-07-04 23:00:17.052999000 Z
  - Hsj
- - system alert
  - 2017-07-04 23:00:19.403000000 Z
  - Student ended session
`}</code></pre>
    <p>{`Who sent a message, when, what it was. Pay attention to the timestamps. `}<inlineCode parentName="p">{`2017-07-04 23:00:02.786000000 Z`}</inlineCode>{` vs. `}<inlineCode parentName="p">{`2017-07-04 23:00:02.786581000 Z`}</inlineCode>{`, for example.`}</p>
    <p>{`That's `}<inlineCode parentName="p">{`0.000581`}</inlineCode>{` seconds apart, half a thousandth of a second. Different enough that our database index relying on timestamps decides these are two different messages.`}</p>
    <p>{`🤔`}</p>
    <p>{`I'm not sure how this bug got introduced, but it boils down to this line somewhere in our webapp code.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`message.set({
  sent_at: moment(message.sent_at),
});
`}</code></pre>
    <p>{`Don't believe me? Watch this.`}</p>
    <pre><code parentName="pre" {...{}}>{`> moment("2017-07-04 23:00:02.786581000 Z").format('YYYY-MM-DDTHH:mm:ss.SSSSSSS')
"2017-07-04T16:00:02.7860000"
`}</code></pre>
    <p>{`😁`}</p>
    <p>{`In fact, this is not even a momentjs problem. JavaScript is limited to milliseconds and iOS is not. Why, I don't know.`}</p>
    <h2 {...{
      "id": "the-solution"
    }}>{`The solution`}</h2>
    <p>{`Change the index!`}</p>
    <p>{`No, not take out `}<inlineCode parentName="p">{`sent_at`}</inlineCode>{`. Heavens no, we still need that. Instead, we can make sure all timestamps have the same precision. We proooooobably don't need to ensure the same message isn't sent twice per nanosecond. Twice per hundredth second should suffice.`}</p>
    <p>{`We introduce a `}<inlineCode parentName="p">{`key`}</inlineCode>{` field and index based on that. Added bonus: improve space performance by hashing the text. Makes the index smaller and maybe faster 🤓`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`def generate_key_if_needed
    if key.blank?
        # round to 1/100s precision
        timestamp = sent_at.to_f.round(2)
        hash = Digest::MurmurHash3_x86_32.hexdigest(text)

        self.key = "#{chat_id}:#{sent_from}:#{timestamp}:#{hash}"
    end
end
`}</code></pre>
    <p><a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/MurmurHash"
      }}>{`MurmurHash`}</a>{` is one of the best algorithms out there for non-cryptographic hashing. That is, one-way hashing that cares about collisions more than guessability.`}</p>
    <h2 {...{
      "id": "fin"
    }}>{`Fin`}</h2>
    <p>{`And that's how you fix what looks like a frontend bug by writing writing code on the backend and re-engineering your biggest database table.`}</p>
    <p>{`Score for the fullstack generalists! 💪🏼`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      